import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { ApiLogDetailModal } from '@/components/api-logs/ApiLogDetailModal';
import { ApiLogFilters } from '@/components/api-logs/ApiLogFilters';
import { OUTCOME_TONES } from '@/components/api-logs/outcome-tones';
import { AlertIcon, ChevronIcon } from '@/components/catalog/icons';
import {
  actionLabel,
  describeActivity,
  moduleLabel,
  type ActivityDescription,
} from '@/lib/activity-language';
import { ApiError, api } from '@/lib/api';
import { actorState, daysAgoLocal, formatLatency, formatLogTotal } from '@/lib/api-log-utils';
import { cx } from '@/lib/cx';
import { formatDateTime, pluralise } from '@/lib/format';
import type {
  ApiLogListParams,
  ApiLogListResponse,
  ApiLogSort,
  ApiLogStatusClass,
  ApiLogSummary,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  Avatar,
  Badge,
  Button,
  Combobox,
  EmptyState,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  type ComboboxOption,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';

/*
 * The audit log — GET /api-logs — read as an activity feed.
 *
 * The rows are the same rows the API has always returned; only the reading of
 * them changed. Instead of METHOD / PATH / STATUS / DURATION, each entry is a
 * sentence produced by `describeActivity` in `lib/activity-language.ts`, and
 * the HTTP facts sit one click away in `ApiLogDetailModal`. There is exactly
 * one humaniser in this app and this file is not it: nothing here invents a
 * verb, an object or an outcome word.
 *
 * Read-only, and permanently so: there is no delete, no prune and no export
 * endpoint in the contract, and there is never going to be one. Nothing on this
 * page writes.
 *
 * Everything the query depends on lives in the URL, so a filtered view is a
 * link someone can paste into a ticket. The effect keys off
 * `searchParams.toString()` rather than the object — `useSearchParams` hands
 * back a fresh instance every render, and depending on it refetches forever.
 *
 * `useSearchParams()` is called with no argument on purpose. Its first
 * parameter is a *default init* for the query string, not an options bag —
 * passing `{ replace: true }` there would inject a literal `replace=true` into
 * the params this page parses. `replace` belongs on each `setSearchParams`
 * call, which is where it is.
 */

const PER_PAGE = 25;
const SKELETON_ROWS = 8;
/** Nothing prunes `f_api_logs`, so an unbounded query scans the whole year. */
const DEFAULT_WINDOW_DAYS = 7;

const STATUS_CLASSES: readonly ApiLogStatusClass[] = ['1xx', '2xx', '3xx', '4xx', '5xx'];
const SORTS: readonly ApiLogSort[] = ['createdAt', 'durationMs', 'statusCode'];

/** Keys this page owns itself; the filter bank never sees them. */
const NON_FILTER_KEYS = ['page', 'perPage', 'sort', 'order'] as const;

/** Stable identity for "no rows", so the memos below don't churn every render. */
const NO_ROWS: readonly ApiLogSummary[] = [];

/* --- query-string parsing ------------------------------------------------ */

/**
 * An empty query parameter is a 422 server-side, not "no filter" — `method=`
 * with no value fails validation rather than defaulting. Blank reads as absent
 * here so a cleared control can leave its key behind harmlessly.
 */
function readString(sp: URLSearchParams, key: string): string | undefined {
  const raw = sp.get(key)?.trim();
  return raw ? raw : undefined;
}

function readInt(sp: URLSearchParams, key: string, min: number, max: number): number | undefined {
  const raw = readString(sp, key);
  if (raw === undefined) return undefined;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < min || value > max) return undefined;
  return value;
}

/** A comma-separated param as its distinct non-blank members. */
function readList(sp: URLSearchParams, key: string): string[] {
  const parts = (readString(sp, key) ?? '').split(',').map((part) => part.trim());
  return [...new Set(parts.filter((part) => part !== ''))];
}

/** Only the two literal strings. Anything else is "not set", never `false`. */
function readBool(sp: URLSearchParams, key: string): boolean | undefined {
  const raw = readString(sp, key);
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return undefined;
}

interface ParsedQuery {
  /** Exactly what goes to the API, minus `page`/`perPage`, which the pager owns. */
  filters: ApiLogListParams;
  page: number;
  sort: ApiLogSort;
  order: 'asc' | 'desc';
  /** True when the 7-day default supplied `from`, rather than the URL. */
  defaultedWindow: boolean;
  /** True when the URL carried both status filters and `statusClass` was dropped. */
  droppedStatusClass: boolean;
}

/**
 * Turns the query string into list params.
 *
 * Two rules do real work here:
 *
 *  - **`from` is always sent.** Retention is unlimited and nothing prunes the
 *    table, so "no window" means scanning every request the system has ever
 *    served. Absent means the last 7 days, and the UI says so out loud rather
 *    than letting a bounded view read as "everything".
 *
 *  - **`statusCode` and `statusClass` are mutually exclusive.** Sending both is
 *    a 422, not an AND. `ApiLogFilters` keeps its own patches exclusive; a
 *    hand-edited or stale URL can still carry both, and resurrecting the pair
 *    here would turn a shared link into an error banner. The exact code wins —
 *    it is the narrower of the two — and the drop is stated on screen.
 */
function parseQuery(sp: URLSearchParams): ParsedQuery {
  const filters: ApiLogListParams = {};

  const from = readString(sp, 'from');
  const defaultedWindow = from === undefined;
  filters.from = from ?? daysAgoLocal(DEFAULT_WINDOW_DAYS);

  const to = readString(sp, 'to');
  if (to !== undefined) filters.to = to;

  /*
   * `userId`, `method`, `moduleSlug` and `actionSlug` are multi-value (owner,
   * 2026-10-28): a comma-separated list in ONE param, sent as the same CSV. A
   * single-value bookmark is a one-item list.
   */
  const userIds = readList(sp, 'userId').filter((part) => {
    const id = Number(part);
    return Number.isInteger(id) && id >= 1;
  });
  if (userIds.length > 0) filters.userId = userIds.join(',');

  const actorEmail = readString(sp, 'actorEmail');
  if (actorEmail !== undefined) filters.actorEmail = actorEmail;

  const anonymous = readBool(sp, 'anonymous');
  if (anonymous !== undefined) filters.anonymous = anonymous;

  const methods = readList(sp, 'method').map((part) => part.toUpperCase());
  if (methods.length > 0) filters.method = [...new Set(methods)].join(',');

  const moduleSlugs = readList(sp, 'moduleSlug');
  if (moduleSlugs.length > 0) filters.moduleSlug = moduleSlugs.join(',');

  const actionSlugs = readList(sp, 'actionSlug');
  if (actionSlugs.length > 0) filters.actionSlug = actionSlugs.join(',');

  const recordId = readString(sp, 'recordId');
  if (recordId !== undefined) filters.recordId = recordId;

  const route = readString(sp, 'route');
  if (route !== undefined) filters.route = route;

  const path = readString(sp, 'path');
  if (path !== undefined) filters.path = path;

  const requestId = readString(sp, 'requestId');
  if (requestId !== undefined) filters.requestId = requestId;

  const statusCode = readInt(sp, 'statusCode', 100, 599);
  const rawStatusClass = readString(sp, 'statusClass');
  const statusClass = STATUS_CLASSES.find((value) => value === rawStatusClass);
  if (statusCode !== undefined) filters.statusCode = statusCode;
  else if (statusClass !== undefined) filters.statusClass = statusClass;
  const droppedStatusClass = statusCode !== undefined && statusClass !== undefined;

  const failed = readBool(sp, 'failed');
  if (failed !== undefined) filters.failed = failed;

  const errorCode = readString(sp, 'errorCode');
  if (errorCode !== undefined) filters.errorCode = errorCode;

  const minDurationMs = readInt(sp, 'minDurationMs', 0, Number.MAX_SAFE_INTEGER);
  if (minDurationMs !== undefined) filters.minDurationMs = minDurationMs;

  const rawSort = readString(sp, 'sort');
  const sort = SORTS.find((value) => value === rawSort) ?? 'createdAt';
  const order = readString(sp, 'order') === 'asc' ? 'asc' : 'desc';
  filters.sort = sort;
  filters.order = order;

  return {
    filters,
    page: readInt(sp, 'page', 1, Number.MAX_SAFE_INTEGER) ?? 1,
    sort,
    order,
    defaultedWindow,
    droppedStatusClass,
  };
}

/** Everything the filter bank is allowed to see and set. */
function filterValue(parsed: ParsedQuery): ApiLogListParams {
  const value: ApiLogListParams = { ...parsed.filters };
  for (const key of NON_FILTER_KEYS) delete value[key];
  return value;
}

/** True once anything narrows the list beyond the default window. */
function hasNarrowingFilter(parsed: ParsedQuery): boolean {
  const value = filterValue(parsed);
  delete value.from;
  return Object.keys(value).length > 0 || !parsed.defaultedWindow;
}

/* --- ordering, in business words ----------------------------------------- */

/**
 * The ordering menu.
 *
 * `sort` accepts exactly three values server-side — `createdAt`, `durationMs`,
 * `statusCode` — and an unknown one is stripped in silence, so every option
 * here maps onto one of the three. What changed is the wording: nobody asking
 * "which of these took the longest" thinks of it as `durationMs desc`. Ordering
 * by status descending puts 5xx above 4xx above 2xx, which is "problems first"
 * in every sense that matters to a reader.
 */
interface OrderOption {
  value: string;
  label: string;
  sort: ApiLogSort;
  order: 'asc' | 'desc';
}

const ORDER_OPTIONS: readonly OrderOption[] = [
  { value: 'createdAt:desc', label: 'Newest first', sort: 'createdAt', order: 'desc' },
  { value: 'createdAt:asc', label: 'Oldest first', sort: 'createdAt', order: 'asc' },
  { value: 'durationMs:desc', label: 'Slowest first', sort: 'durationMs', order: 'desc' },
  { value: 'durationMs:asc', label: 'Fastest first', sort: 'durationMs', order: 'asc' },
  { value: 'statusCode:desc', label: 'Problems first', sort: 'statusCode', order: 'desc' },
  { value: 'statusCode:asc', label: 'Successes first', sort: 'statusCode', order: 'asc' },
];

/**
 * The same six as `Combobox` rows — searchable, per the owner's 2026-09-13
 * ruling that every filter dropdown in this app is a `Combobox` whatever its
 * option count.
 *
 * ⚠️ Mapped rather than handed over as-is: `ORDER_OPTIONS` is `readonly` where
 * `ComboboxOption[]` is mutable, and it carries `sort`/`order` besides. Taking
 * only the two keys a row needs also keeps `handleOrder` the single place a
 * chosen value is resolved back into a `{sort, order}` pair, so this list and
 * that lookup cannot drift apart about what a value means.
 *
 * ⚠️ No `emptyOptionLabel`: ordering is always in force. `sort` is clamped to
 * the three members server-side and `order` parses to `asc`/`desc`, so
 * `${sort}:${order}` is always one of these six and the trigger never falls back
 * to a placeholder.
 */
const ORDER_COMBOBOX_OPTIONS: ComboboxOption[] = ORDER_OPTIONS.map(({ value, label }) => ({
  value,
  label,
}));

/* --- outcome presentation ------------------------------------------------ */

/*
 * Tone per outcome lives in `@/components/api-logs/outcome-tones` — ONE copy,
 * shared with the detail modal (the two local copies had drifted).
 */

/**
 * A sign-in attempt that did not succeed.
 *
 * Singled out because it is the one line on this page that is security news
 * rather than operations news: a 401 on `/api/v1/auth/login` is somebody
 * failing to get in. A 422 or a 429 on the same route is the same story told
 * differently, so the test is "any failure on the login route" rather than the
 * exact code. `route` is the matched Express pattern and is the reliable side;
 * `path` carries a query string, so it is compared with that stripped.
 */
function isFailedSignIn(log: ApiLogSummary): boolean {
  if (log.statusCode < 400) return false;
  const target = log.route ?? log.path.split('?')[0] ?? '';
  return /\/auth\/login\/?$/.test(target);
}

/* --- day grouping -------------------------------------------------------- */

/**
 * The LOCAL calendar day of a timestamp, as 'YYYY-MM-DD'.
 *
 * Local parts, never `toISOString().slice(0, 10)` — that is UTC, and it files
 * an 11pm entry under tomorrow for anyone east of Greenwich. Grouping by the
 * wrong day is the kind of error nobody notices until they are reconstructing
 * an incident.
 */
function dayKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** 'Today', 'Yesterday', then a written date — with the year only when it differs. */
function dayHeading(date: Date, todayKey: string, yesterdayKey: string): string {
  const key = dayKey(date);
  if (key === todayKey) return 'Today';
  if (key === yesterdayKey) return 'Yesterday';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

interface FeedEntry {
  log: ApiLogSummary;
  description: ActivityDescription;
  failedSignIn: boolean;
  when: Date;
}

interface FeedGroup {
  key: string;
  heading: string;
  entries: FeedEntry[];
}

/**
 * Buckets entries into day groups, breaking on a CHANGE of day rather than
 * collecting every entry with the same date.
 *
 * That distinction matters because the list is not always chronological: order
 * by "slowest first" and the days interleave, at which point a real grouping
 * would print "Today" three times in three places. The caller only asks for day
 * grouping when the sort is chronological; this stays run-based anyway so that
 * a same-day-but-out-of-order page can never silently reorder what the API
 * returned.
 */
function groupByDay(entries: readonly FeedEntry[]): FeedGroup[] {
  const now = new Date();
  const todayKey = dayKey(now);
  const yesterdayKey = dayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));

  const groups: FeedGroup[] = [];
  for (const entry of entries) {
    const key = dayKey(entry.when);
    const last = groups[groups.length - 1];
    if (last && last.key.startsWith(`${key}#`)) {
      last.entries.push(entry);
      continue;
    }
    groups.push({
      key: `${key}#${groups.length}`,
      heading: dayHeading(entry.when, todayKey, yesterdayKey),
      entries: [entry],
    });
  }
  return groups;
}

/* --- one entry ----------------------------------------------------------- */

/**
 * The sentence, with the object turned into a link where there is a page to go
 * to.
 *
 * Rendered by splitting the humaniser's own `sentence` rather than
 * re-assembling it from `actor` + `verb` + `object`: the wording is
 * `activity-language`'s to decide, including how a deleted account is
 * attributed, and stitching the parts back together here is how a second,
 * subtly different humaniser gets born. The actor prefix is lifted into a
 * stronger weight and the object is wrapped — no words are added, removed or
 * reordered.
 *
 * `href` is null whenever there is no page for the record, and a null href
 * renders as plain text. Nothing here fabricates a route.
 */
function ActivitySentence({
  description,
  /**
   * True when the Who column is rendering the actor, so the sentence must not
   * repeat it. The prefix is LIFTED, never re-worded: `description.actor` is
   * documented as an exact prefix of `sentence`, and this is the same split the
   * component already performed to give the name a stronger weight.
   *
   * When the sentence does not lead with the actor — the humaniser phrases some
   * rows as events rather than as actions — nothing is stripped and the full
   * sentence renders, which is why this is a hint and not an instruction.
   */
  actorShownSeparately = false,
}: {
  description: ActivityDescription;
  actorShownSeparately?: boolean;
}) {
  const { actor, sentence, object, href } = description;

  const leadsWithActor = actor.length > 0 && sentence.startsWith(actor);
  const lifted = leadsWithActor && actorShownSeparately;
  /*
   * The remainder after the prefix. `trimStart` runs only when the Who column
   * has taken the name, so the sentence does not open with the gap the lift
   * leaves behind — whitespace, never a word. The `object` search below still
   * works because it is a `lastIndexOf` on this same string.
   */
  const rest = leadsWithActor
    ? lifted
      ? sentence.slice(actor.length).trimStart()
      : sentence.slice(actor.length)
    : sentence;

  let body: ReactNode = rest;
  if (object !== null && object !== '' && href !== null) {
    const at = rest.lastIndexOf(object);
    const link = (
      <TextLink to={href}>
        {object}
      </TextLink>
    );
    body =
      at >= 0 ? (
        <>
          {rest.slice(0, at)}
          {link}
          {rest.slice(at + object.length)}
        </>
      ) : (
        <>
          {rest} {link}
        </>
      );
  }

  return (
    <p className="m-0 min-w-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
      {leadsWithActor && !lifted && <span className="font-medium text-fg">{actor}</span>}
      {body}
    </p>
  );
}

/**
 * The Who cell — the person, in a column of their own.
 *
 * ⚠️ THE FEED USED TO CARRY THE ACTOR ONLY INSIDE THE SENTENCE, in a stronger
 * weight. That is correct prose and it does not SCAN: reading "which of my
 * colleagues did what" meant reading the opening words of every row, and the
 * names sat at whatever column the previous row's timestamp left them at. A
 * fixed column puts every name on one vertical line.
 *
 * ⚠️ THE NAME IS NOT RE-WORDED HERE. `description.actor` is the humaniser's own
 * string and is documented as an exact prefix of `sentence`, so the cell and the
 * lifted sentence are one derivation split in two, not two humanisers. The only
 * edit is the TRAILING COMMA a support session's actor carries to close its
 * clause — "Moin, acting as Anvita," — which is punctuation for a sentence this
 * cell is no longer part of.
 *
 * ⚠️ A SUPPORT SESSION IS NOT FILTERABLE FROM HERE, deliberately. Two people are
 * named and `log.actor` is only one of them — the account the request RAN IN —
 * so a click would silently narrow to the target while the cell reads as the
 * operator's action. The row keeps its badge and its full sentence instead.
 */
function ActorCell({
  entry,
  onFilter,
}: {
  entry: FeedEntry;
  onFilter: (patch: Partial<ApiLogListParams>) => void;
}) {
  const { log, description } = entry;
  const state = actorState(log.actor);
  const isSupport = description.supportSession;

  // Punctuation only. See the note above.
  const label = description.actor.replace(/,\s*$/, '');

  /*
   * What clicking asks for. A live account filters by id; a deleted one by the
   * denormalised email, which is the only identity it has left and exactly what
   * the email filter was kept for. Anything else — anonymous, unknown, a
   * support session — is not a person this feed can be narrowed to, so it is
   * rendered as plain text rather than as a control that would do nothing.
   */
  const patch: Partial<ApiLogListParams> | null = isSupport
    ? null
    : state.kind === 'user'
      ? { userId: state.id, actorEmail: undefined }
      : state.kind === 'deleted'
        ? { actorEmail: state.email, userId: undefined }
        : null;

  const inner = (
    <>
      {state.kind === 'user' && !isSupport && <Avatar name={state.name} size="sm" />}
      <span className="min-w-0 [overflow-wrap:anywhere]">{label}</span>
    </>
  );

  const shared = 'flex min-w-0 items-center gap-1.5 text-left text-xs font-medium text-fg';

  if (patch === null) {
    return <span className={cx(shared, 'text-fg-secondary')}>{inner}</span>;
  }

  return (
    <button
      type="button"
      onClick={() => onFilter(patch)}
      title={`Show only what ${label} did`}
      className={cx(
        shared,
        'rounded-sm underline-offset-2 hover:underline',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
      )}
    >
      {inner}
    </button>
  );
}

/**
 * The HTTP facts, shown inline only when the reader has asked for them.
 *
 * This is the density escape hatch, not a second table: same rows, same order,
 * one extra line each. Anything beyond these five values — bodies, redaction
 * notes, request id, IP, user agent — lives in the detail modal, which already
 * renders them correctly.
 */
function TechnicalLine({ log }: { log: ApiLogSummary }) {
  const area = [moduleLabel(log.module), actionLabel(log.action)].filter(Boolean).join(' · ');

  return (
    <p className="m-0 mt-0.5 flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-2xs text-fg-muted">
      <span className="font-mono font-semibold text-fg-secondary">{log.method}</span>
      <span className="min-w-0 font-mono [overflow-wrap:anywhere]" title={log.path}>
        {log.path}
      </span>
      <span className="tabular">{log.statusCode}</span>
      <span className="tabular">{formatLatency(log.durationMs)}</span>
      {area !== '' && <span className="min-w-0 [overflow-wrap:anywhere]">{area}</span>}
    </p>
  );
}

/**
 * One line of the feed.
 *
 * Below `sm` the three parts stack — time, sentence, outcome — so 320px never
 * pushes the page sideways. `min-w-0` sits on every flex child that holds text
 * for the same reason: a flex child defaults to `min-width: auto` and refuses
 * to shrink below its content.
 */
function FeedRow({
  entry,
  showTechnical,
  timeLabel,
  timeClass,
  onOpen,
  onFilterActor,
}: {
  entry: FeedEntry;
  showTechnical: boolean;
  timeLabel: string;
  /*
   * A literal utility string, never interpolated. Tailwind scans source text
   * for whole class names, so `sm:${width}` produces a class that is never
   * generated and a column that never lines up.
   */
  timeClass: string;
  onOpen: () => void;
  onFilterActor: (patch: Partial<ApiLogListParams>) => void;
}) {
  const { log, description, failedSignIn } = entry;
  /*
   * Only for an ordinary row. On a support session `log.actor` is one of two
   * people and the badge could not say which — while the sentence already spells
   * out "a deleted account (…)" against whichever party it belongs to. A badge
   * that names a state without naming whose it is reads as a fact about the row
   * and is worse than no badge.
   */
  const deleted = !description.supportSession && actorState(log.actor).kind === 'deleted';

  return (
    <li
      className={cx(
        'min-w-0 border-b border-border last:border-b-0',
        failedSignIn && 'border-l-2 border-l-danger bg-danger-soft',
      )}
    >
      <div className="flex min-w-0 flex-col gap-1 px-4 py-2 sm:flex-row sm:items-baseline sm:gap-3">
        <time
          dateTime={log.createdAt}
          title={formatDateTime(log.createdAt)}
          className={cx('shrink-0 whitespace-nowrap text-2xs text-fg-muted tabular', timeClass)}
        >
          {timeLabel}
        </time>

        {/*
         * The Who column. Fixed width from `sm` so names line up down the page;
         * below it the cell stacks above the sentence, which is the same
         * treatment the time already gets and is why 320px never scrolls
         * sideways. `min-w-0` on both, because a flex child defaults to
         * `min-width: auto` and refuses to shrink below its content.
         */}
        <div className="min-w-0 shrink-0 sm:w-44">
          <ActorCell entry={entry} onFilter={onFilterActor} />
        </div>

        <div className="min-w-0 flex-1">
          <ActivitySentence description={description} actorShownSeparately />
          {showTechnical && <TechnicalLine log={log} />}
        </div>

        <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-1.5 sm:justify-end">
          {failedSignIn && <Badge tone="danger">Failed sign-in</Badge>}
          {/*
           * A SECOND signal, never the only one. The sentence already names both
           * people — the badge is here so a reader scanning a page of rows can
           * see which ones were taken inside a support session without reading
           * every sentence, and so the row still announces itself if the
           * wording is ever truncated by a narrow column.
           */}
          {description.supportSession && <Badge tone="warning">Support session</Badge>}
          {deleted && <Badge tone="warning">Deleted user</Badge>}
          {/* Toned, never tone-only — the word is always present. */}
          <Badge tone={OUTCOME_TONES[description.outcome]} dot>
            {description.outcomeLabel}
          </Badge>
          {/*
           * The way into every HTTP fact this row hides. It opens the existing
           * detail modal rather than expanding inline: the modal already
           * handles the logged bodies, the redaction note and the truncation
           * marker, and none of that is worth a second implementation.
           */}
          <Button
            variant="ghost"
            size="sm"
            className="-mr-2.5"
            onClick={onOpen}
            aria-label={`Show technical details for ${description.sentence}`}
            title={`Show technical details — ${log.method} ${log.path}`}
          >
            {/* Muted: it repeats on every row, so it must not out-shout the
                sentence it sits beside. Style only — the wording is unchanged. */}
            <span className="font-normal text-fg-muted max-sm:hidden">Show technical details</span>
            <span className="font-normal text-fg-muted sm:hidden">Details</span>
          </Button>
        </div>
      </div>
    </li>
  );
}

/** Skeletons in the shape of the real feed — a day heading and its rows. */
function FeedSkeleton() {
  return (
    <div className="min-w-0">
      <div className="border-b border-border bg-surface-sunken px-4 py-1.5">
        <Skeleton className="h-3 w-20" />
      </div>
      <ol className="m-0 list-none p-0">
        {Array.from({ length: SKELETON_ROWS }, (_, i) => (
          <li key={i} className="min-w-0 border-b border-border last:border-b-0">
            <div className="flex min-w-0 flex-col gap-1 px-4 py-2.5 sm:flex-row sm:items-center sm:gap-3">
              <Skeleton className="h-3 w-12 shrink-0" />
              <Skeleton className="h-3 w-full max-w-[20rem] sm:flex-1" />
              <Skeleton className="h-4 w-20 shrink-0" />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* --- page ---------------------------------------------------------------- */

export function ApiLogsPage() {
  const { ability } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState<ApiLogListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** A 403 is a role change under a live token, not a validation failure. */
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openLogId, setOpenLogId] = useState<number | null>(null);
  /*
   * Two presentation switches, deliberately NOT in the URL: they change how the
   * loaded page reads, not what was asked of the API, and a shared link should
   * carry the query, not one reader's density preference. Remembered on the
   * account instead (owner 2026-10-16, "every filter/sort persists").
   */
  const [hideRoutine, setHideRoutine] = useRememberedState('hide-routine', true);
  const [showTechnical, setShowTechnical] = useRememberedState('show-technical', false);

  // The audit log is admin-tier even to READ — `enforcedByRole` returns `admin`
  // for every action on `api-logs`, so this one check covers the whole page.
  const canRead = ability.can('read', 'api-logs');

  const queryString = searchParams.toString();
  const parsed = useMemo(() => parseQuery(new URLSearchParams(queryString)), [queryString]);
  const { page, sort, order } = parsed;

  const load = useCallback(
    (signal?: AbortSignal) => {
      const current = parseQuery(new URLSearchParams(queryString));
      setLoading(true);
      return api
        .listApiLogs({ ...current.filters, page: current.page, perPage: PER_PAGE }, signal)
        .then((data) => {
          setResult(data);
          setError(null);
          setForbidden(false);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          /*
           * 403 carries no `details` — unlike a 422 there is nothing to hang on
           * a field, and no field to hang it on. It means the role behind this
           * still-valid token no longer reaches the audit log, so the whole
           * surface goes away rather than one control.
           */
          if (caught instanceof ApiError && caught.status === 403) {
            setForbidden(true);
            setError(null);
            setResult(null);
            return;
          }
          setForbidden(false);
          setError(caught instanceof Error ? caught.message : 'Failed to load the audit log');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [queryString],
  );

  useEffect(() => {
    // No request at all without the role — the denied state below renders
    // instead, and it never reads `loading`.
    if (!canRead) return;
    const controller = new AbortController();
    // Debounced like every other filtered list here: the filter bank writes to
    // the URL as you type, and a path substring must not be a request per
    // keystroke.
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [canRead, load]);

  /**
   * Writes a filter patch into the URL.
   *
   * `undefined` deletes — `Object.entries` keeps a key explicitly set to
   * `undefined`, which is how `ApiLogFilters` clears the other half of the
   * statusCode/statusClass pair in the same patch that sets one of them.
   */
  const applyPatch = useCallback(
    (patch: Partial<ApiLogListParams>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === null || value === '') next.delete(key);
        else next.set(key, String(value));
      }
      // Any change to what is being asked for invalidates the offset.
      if (!('page' in patch)) next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const goToPage = useCallback(
    (target: number) => {
      const next = new URLSearchParams(searchParams);
      if (target <= 1) next.delete('page');
      else next.set('page', String(target));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleOrder = useCallback(
    (value: string) => {
      const option = ORDER_OPTIONS.find((candidate) => candidate.value === value);
      // An unrecognised value would otherwise post `sort=undefined` to the API.
      if (!option) return;
      applyPatch({ sort: option.sort, order: option.order });
    },
    [applyPatch],
  );

  const rows = result?.data ?? NO_ROWS;
  const total = result?.meta.total ?? 0;
  const totalIsExact = result?.meta.totalIsExact ?? true;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const initialLoad = loading && !result;
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  /*
   * `total` caps at 10,000 and `totalPages` is derived from it, so both are
   * FLOORS when `totalIsExact` is false. Stopping Next on a floored last page
   * would hide real rows, so the pager only stops on a page it can prove is the
   * last one.
   */
  const atLastPage = rows.length < PER_PAGE || (totalIsExact && page >= totalPages);
  const filtered = hasNarrowingFilter(parsed);

  /*
   * One `describeActivity` call per row, once. Everything downstream — the
   * routine count, the failed-sign-in count, the grouping — reads this list, so
   * the number the notice quotes and the rows actually on screen can never
   * disagree.
   */
  const entries = useMemo<FeedEntry[]>(
    () =>
      rows.map((log) => ({
        log,
        description: describeActivity(log),
        failedSignIn: isFailedSignIn(log),
        when: new Date(log.createdAt),
      })),
    [rows],
  );

  /*
   * A failed sign-in is never treated as routine, whatever the humaniser says
   * about it. Hiding the one entry a reader came here for behind a "routine"
   * collapse is the failure mode this whole feature has to avoid.
   */
  const hidable = useMemo(
    () => entries.filter((entry) => entry.description.routine && !entry.failedSignIn),
    [entries],
  );
  const routineCount = hidable.length;
  const failedSignInCount = entries.filter((entry) => entry.failedSignIn).length;

  const visible = useMemo(
    () =>
      hideRoutine
        ? entries.filter((entry) => !entry.description.routine || entry.failedSignIn)
        : entries,
    [entries, hideRoutine],
  );

  /*
   * Day headings only make sense while the list runs in time order. Under
   * "slowest first" the days interleave, so the feed drops to a single run and
   * every row carries its full date instead — a repeated "Today" three screens
   * apart is worse than no heading at all.
   */
  const groupedByDay = sort === 'createdAt';
  const groups = useMemo(
    () =>
      groupedByDay
        ? groupByDay(visible)
        : visible.length > 0
          ? [{ key: 'all', heading: 'In this window', entries: visible }]
          : [],
    [groupedByDay, visible],
  );

  const routineNotice =
    routineCount === 0
      ? null
      : hideRoutine
        ? `${pluralise(routineCount, 'routine entry', 'routine entries')} hidden`
        : `${pluralise(routineCount, 'routine entry', 'routine entries')} shown`;

  const countLabel = initialLoad
    ? 'Loading…'
    : forbidden
      ? 'Unavailable to this account'
      : `${formatLogTotal(total, totalIsExact)} ${
          total === 1 && totalIsExact ? 'request' : 'requests'
        } in this window`;

  const statsWindow = {
    from: parsed.filters.from ?? daysAgoLocal(DEFAULT_WINDOW_DAYS),
    ...(parsed.filters.to ? { to: parsed.filters.to } : {}),
  };

  /*
   * Two ways into the same page, because they answer different questions and
   * the second one was previously reachable only by changing a dropdown after
   * arriving. "Everyone" lands on the by-person breakdown — a list of people
   * with a count each, every row already a link back into this feed filtered to
   * them — which is the shape somebody asking "who has been doing what" wants,
   * and it is not discoverable from a link that says "View stats".
   */
  const statsLinks = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <TextLink
        to={`/audit-log/stats?${new URLSearchParams({
          ...statsWindow,
          groupBy: 'userId',
        }).toString()}`}
        className="inline-flex items-center gap-1 text-sm font-medium"
      >
        Everyone, with counts
        <ChevronIcon direction="right" />
      </TextLink>
      <TextLink
        to={`/audit-log/stats?${new URLSearchParams(statsWindow).toString()}`}
        className="inline-flex items-center gap-1 text-sm font-medium"
      >
        View stats
        <ChevronIcon direction="right" />
      </TextLink>
    </div>
  );

  /*
   * The permission check, not an empty feed. `api-logs` is admin-tier for
   * every action including read, so a non-admin who lands on this URL gets a
   * plain statement instead of a feed that looks like the system has never
   * served a request.
   */
  if (!canRead) {
    return (
      <>
        <PageHeader eyebrow="Administration" title="Audit log" />
        <Panel className="min-w-0">
          <EmptyState
            icon={<AlertIcon />}
            title="The audit log isn’t available to your account"
            description="Administrators only. Ask one if you need something from it."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Audit log"
        actions={statsLinks}
      />

      <Panel className="min-w-0">
        <PanelHeader title="Activity" description={countLabel} />

        {/*
         * The filter bank gets its own full-width row rather than PanelHeader's
         * `actions` slot: that slot is `shrink-0`, so a bank of controls in it
         * can neither shrink nor wrap, and blows the page out sideways long
         * before 320px.
         */}
        <div className="min-w-0 border-b border-border px-4 py-3">
          <ApiLogFilters
            value={filterValue(parsed)}
            onChange={applyPatch}
            onReset={reset}
            busy={loading}
          />
        </div>

        {/* Ordering and density. Both wrap; neither is allowed to set a width
            floor that a 320px viewport cannot honour. */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-2">
          <label htmlFor="activity-order" className="text-xs text-fg-muted">
            Order
          </label>
          <div className="w-full min-w-0 sm:w-44">
            {/* ⚠️ **THE WRAPPER IS THE ONLY THING THAT SIZES THIS.** `Combobox`
                accepts no `className` at all, so there is no prop through which
                a width or height could be passed; its trigger is `w-full h-9`
                (`max-sm:h-11`) and takes the wrapper's width.

                *SUPERSEDED 2026-09-13, when this was a `Select`: "No height or
                size override: conflicting Tailwind utilities resolve by
                stylesheet order, not attribute order, so an `h-8` passed in here
                would silently lose to the `h-9` baked into Select."* The hazard
                is not merely moot — the stylesheet-order rule it names is
                exactly why `Combobox` must not GAIN a `className` escape hatch
                later. A size passed in would lose the same race, silently, and
                the control would look correct in the diff. */}
            <Combobox
              id="activity-order"
              value={`${sort}:${order}`}
              onChange={handleOrder}
              options={ORDER_COMBOBOX_OPTIONS}
              searchPlaceholder="Search ordering…"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="sm:ml-auto"
            aria-pressed={showTechnical}
            onClick={() => setShowTechnical((value) => !value)}
            title="Show method, path, status and duration"
          >
            {showTechnical ? 'Hide request details' : 'Show request details'}
          </Button>
        </div>

        {/*
         * Retention is unlimited and nothing prunes the table, so a window is
         * always sent. Saying which one is not optional — a bounded view read as
         * "everything" is how someone concludes an action never happened.
         */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border px-4 py-2">
          <Badge tone="neutral" variant="outline">
            Window
          </Badge>
          <p className="m-0 min-w-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
            {parsed.defaultedWindow
              ? `Last ${DEFAULT_WINDOW_DAYS} days (from ${parsed.filters.from}), not the whole log.`
              : `From ${parsed.filters.from}${
                  parsed.filters.to ? ` to ${parsed.filters.to} (inclusive)` : ' to now'
                } only.`}
          </p>
        </div>

        {parsed.droppedStatusClass && (
          <Notice tone="warning" role="status" className="mx-4 my-2" title="Status class ignored.">
            <span className="min-w-0 [overflow-wrap:anywhere]">
              Used the exact code
              {parsed.filters.statusCode !== undefined ? ` (${parsed.filters.statusCode})` : ''};
              the two cannot combine.
            </span>
          </Notice>
        )}

        {/*
         * The routine collapse announces itself, always. This is an audit log:
         * a view that quietly drops entries is the one thing it must never be,
         * so the count is exact for the loaded page, the reason is spelled out,
         * and the control to see them is right there.
         *
         * `describeActivity` decides what counts as routine — currently GET
         * reads plus the health-check and token-refresh routes, i.e. nothing
         * that changed state. The wording below tracks that rule; it must not
         * claim a narrower one than the humaniser actually applies.
         */}
        {routineNotice !== null && (
          <div
            role="status"
            className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 border-b border-border bg-surface-sunken px-4 py-2"
          >
            <span className="text-xs font-semibold text-fg-secondary">{routineNotice}</span>
            <span className="min-w-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
              on this page — look-ups, health checks, renewals. Nothing that changed data is hidden.
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="-my-1"
              aria-pressed={!hideRoutine}
              onClick={() => setHideRoutine((value) => !value)}
            >
              {hideRoutine ? 'Show' : 'Hide'}
            </Button>
          </div>
        )}

        {/*
         * Failed sign-ins get their own line above the feed as well as their own
         * treatment in it. Everything else on this page is operations; this is
         * the only thing on it that is security.
         */}
        {failedSignInCount > 0 && (
          <Notice
            tone="danger"
            role="status"
            className="mx-4 my-2"
            title={<>{pluralise(failedSignInCount, 'failed sign-in')} on this page.</>}
          >
            <span className="min-w-0 [overflow-wrap:anywhere]">
              Each one is marked below.
            </span>
          </Notice>
        )}

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
          {routineNotice !== null ? `. ${routineNotice}.` : ''}
          {failedSignInCount > 0 ? ` ${pluralise(failedSignInCount, 'failed sign-in')}.` : ''}
        </p>

        {forbidden && (
          <Notice
            tone="danger"
            role="alert"
            className="m-4"
            title="The audit log is no longer available to you."
          >
            <span className="min-w-0 [overflow-wrap:anywhere]">
              Your role may have changed. Sign out and back in, or ask an administrator.
            </span>
          </Notice>
        )}

        {error && (
          <Notice tone="danger" role="alert" className="m-4" title="Couldn’t load the audit log.">
            <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        {/*
         * `min-w-0` on the element wrapping the feed: a grid or flex child
         * defaults to `min-width: auto`, which makes it refuse to shrink below
         * its content and pushes the whole page sideways instead of wrapping.
         * That has broken this app below 768px before.
         */}
        <div
          aria-busy={loading || undefined}
          className={cx(
            'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {initialLoad ? (
            <FeedSkeleton />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<AlertIcon />}
              title={
                forbidden || error
                  ? 'Nothing to show'
                  : filtered
                    ? 'Nothing matches these filters'
                    : 'No activity in this window'
              }
              description={
                forbidden || error
                  ? 'Check the message above and try again.'
                  : filtered
                    ? 'Widen the date window, or drop a filter.'
                    : `Nothing recorded in the last ${DEFAULT_WINDOW_DAYS} days. Widen the window to go further back.`
              }
              action={
                filtered && !forbidden ? (
                  <Button variant="secondary" size="sm" onClick={reset}>
                    Reset to the last {DEFAULT_WINDOW_DAYS} days
                  </Button>
                ) : null
              }
            />
          ) : visible.length === 0 ? (
            /* Everything on this page was routine. Say so plainly rather than
               rendering an empty feed that reads as "nothing happened". */
            <EmptyState
              icon={<AlertIcon />}
              title="Only routine entries on this page"
              description={`All ${pluralise(rows.length, 'entry', 'entries')} here are routine, and routine is hidden by default. Nothing on this page changed data.`}
              action={
                <Button variant="secondary" size="sm" onClick={() => setHideRoutine(false)}>
                  Show routine entries
                </Button>
              }
            />
          ) : (
            <div className="min-w-0">
              {groups.map((group) => (
                <section
                  key={group.key}
                  aria-labelledby={`activity-day-${group.key}`}
                  className="min-w-0"
                >
                  <h3
                    id={`activity-day-${group.key}`}
                    className="m-0 flex min-w-0 flex-wrap items-baseline gap-x-2 border-b border-border bg-surface-sunken px-4 py-1.5 text-xs font-semibold text-fg-secondary"
                  >
                    <span className="min-w-0 [overflow-wrap:anywhere]">{group.heading}</span>
                    <span className="font-normal text-fg-muted tabular">
                      {pluralise(group.entries.length, 'entry', 'entries')}
                    </span>
                  </h3>
                  {/* An ordered list: the sequence is the content. Markers are
                      suppressed, the semantics are not. */}
                  <ol className="m-0 min-w-0 list-none p-0">
                    {group.entries.map((entry) => (
                      <FeedRow
                        key={entry.log.id}
                        entry={entry}
                        showTechnical={showTechnical}
                        timeLabel={
                          groupedByDay
                            ? entry.when.toLocaleTimeString(undefined, {
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : formatDateTime(entry.log.createdAt)
                        }
                        timeClass={groupedByDay ? 'sm:w-16' : 'sm:w-40'}
                        onOpen={() => setOpenLogId(entry.log.id)}
                        onFilterActor={applyPatch}
                      />
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          )}
        </div>

        {result && (
          <PanelFooter>
            <p className="m-0 min-w-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {total === 0
                ? 'Nothing to show'
                : `Showing ${firstRow}–${lastRow} of ${formatLogTotal(total, totalIsExact)}`}
              {!totalIsExact && (
                <span className="block text-fg-subtle sm:ml-1 sm:inline">
                  · the count stops at 10,000
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              {/* "Page 3 of 400" is a lie when 400 is a floor — the "+" stays. */}
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {page} of {totalPages}
                {totalIsExact ? '' : '+'}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={atLastPage}
                onClick={() => goToPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>

      <ApiLogDetailModal logId={openLogId} onClose={() => setOpenLogId(null)} />
    </>
  );
}
